import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./Login.module.css";
import { CustomTextInput } from "../../components";
import { useAuth } from "../../hooks/useAuth";
import shape1 from "../../assets/images/Home/login.png";
import logo1 from "../../assets/images/ifillip-logo.png";
import PopupModal from "../../components/PopupModal/PopupModal";
import { TokenService, TenantService } from "../../services/Api";

const PLAY_STORE_LINK =
  "https://play.google.com/store/apps/details?id=com.ifillip.school&hl=en_IN&gl=US";

const Login = () => {
  const navigate = useNavigate();
  const {
    login,
    completeLoginFlow,
    isAuthenticated,
    loading: authLoading,
  } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    subDomain: "",
    rememberMe: false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
    redirectPath: "",
  });
  // Redirect to Play Store for Android devices
  // useEffect(() => {
  //   const isAndroid = /android/i.test(navigator.userAgent);
  //   if (isAndroid) {
  //     window.location.href = PLAY_STORE_LINK;
  //     return;
  //   }
  //   const timer = setTimeout(() => setIsLoading(false), 500);
  //   return () => clearTimeout(timer);
  // }, []);
  // 🧹 Clear any leftover tenant from a previous login so returning to
  // this screen and logging in WITHOUT a subdomain starts clean.
  useEffect(() => {
    TenantService.removeTenant();
  }, []);
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    // 🚫 Remove emojis (Unicode emoji ranges)
    if (type !== "checkbox") {
      newValue = newValue.replace(
        /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu,
        "",
      );
      if (name === "subDomain") {
        newValue = newValue.toLowerCase().replace(/[^a-z0-9]/g, "");
      }
    }
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    // Clear API error when user modifies form
    if (apiError) {
      setApiError("");
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Validate on blur
    const fieldError = validateField(name, formData[name]);
    if (fieldError) {
      setErrors((prev) => ({ ...prev, [name]: fieldError }));
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case "email":
        if (!value) return "Email is required";
        return "";
      case "password":
        if (!value) return "Password is required";
        if (value.length < 6) {
          return "Password must be at least 6 characters";
        }
        return "";
      default:
        return "";
    }
  };

  const validate = () => {
    const newErrors = {};
    const emailError = validateField("email", formData.email);
    const passwordError = validateField("password", formData.password);

    if (emailError) newErrors.email = emailError;
    if (passwordError) newErrors.password = passwordError;

    return newErrors;
  };

  const isFormValid =
    formData.email && formData.password && !errors.email && !errors.password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    setTouched({ email: true, password: true });

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // 🔑 Clear any stale auth state before a fresh login attempt.
    // Prevents the "stuck logged-in with invalid token" flicker loop.
    TokenService.clearAll();
    TenantService.setTenant(formData.subDomain);

    setIsSubmitting(true);

    try {
      const result = await login({
        email: formData.email,
        password: formData.password,
        subDomain: formData.subDomain,
      });

      if (result.success) {
        const roleId = Number(result.user.role.id);
        const isParentOrStudent = roleId === 6 || roleId === 4;
        const isPasswordAllNumeric = /^\d+$/.test(formData.password);

        if (isParentOrStudent && isPasswordAllNumeric) {
          navigate(`/change-password/${encodeURIComponent(formData.email)}`, {
            replace: true,
          });
          return;
        }

        TokenService.setToken(result.token);
        TokenService.setUser(result.user);

        setModalState({
          open: true,
          type: "success",
          message: result.message || "Login successful!",
          redirectPath: roleId === 1 ? "/dashboard" : "/user/dashboard",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: result.message || "Login failed. Please try again.",
          redirectPath: "",
        });
        setApiError(result.message || "Login failed. Please try again.");
      }
    } catch (error) {
      console.error("Login error:", error);
      setApiError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHomeClick = () => {
    navigate("/");
  };

  // Helper to get error for a field (only show if touched)
  const getFieldError = (fieldName) => {
    return touched[fieldName] ? errors[fieldName] : "";
  };

  // Show loader while checking auth or page loading
  if (isLoading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.loader}></div>
      </div>
    );
  }
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;

    setModalState((prev) => ({
      ...prev,
      open: false,
    }));

    if (type === "success" && redirectPath) {
      completeLoginFlow(); // ✅ VERY IMPORTANT
      const sub = formData.subDomain?.trim().toLowerCase();
      if (sub) {
        // Tenant entered → open the tenant host in a NEW TAB
        window.open(
          `https://${sub}.ifillip.com${redirectPath}`,
          "_blank",
          "noopener,noreferrer",
        );
        setFormData({});
      } else {
        navigate(redirectPath, { replace: true });
      }
    }
  };
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.mainPageWrapper}>
        <div className={styles.logSection}>
          <div className={styles.container}>
            <div className={styles.loginBox}>
              {/* Left Column */}
              <div className={styles.leftColumn}>
                <div className={styles.brandingContent}>
                  <img
                    src={shape1}
                    alt="Education Illustration"
                    className={styles.illustrationImage}
                  />
                  <div className={styles.logoContainer}>
                    <img
                      src={logo1}
                      alt="iFillip Logo"
                      className={styles.logo}
                    />
                  </div>
                  <p className={styles.tagline}>
                    An omni-channel school management and learning augmentation
                    platform
                  </p>
                  <a
                    href="https://bharatclouds.com/"
                    className={styles.companyName}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    A Product of Bharat Clouds Pvt Ltd
                  </a>
                </div>
              </div>

              {/* Right Column */}
              <div className={styles.rightColumn}>
                <h2 className={styles.loginTitle}>Login</h2>
                <p className={styles.disclaimer}>
                  This is a restricted network. Use of this network, its
                  equipment, and resources is monitored at all times and
                  requires explicit permission from the network administrator.
                </p>

                {/* API Error Message */}
                {apiError && (
                  <div className={styles.apiError}>
                    <span className={styles.errorIcon}>⚠</span>
                    {apiError}
                  </div>
                )}

                <form
                  name="sign_in"
                  id="sign_in"
                  onSubmit={handleSubmit}
                  className={styles.loginForm}
                  noValidate
                >
                  {/* Email Input */}
                  <CustomTextInput
                    type="email"
                    name="email"
                    placeholder="Enter Your Email ID"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={getFieldError("email")}
                    maxLength={100}
                    autoComplete="email"
                    required
                    disabled={isSubmitting}
                  />

                  {/* Password Input */}
                  <CustomTextInput
                    type="password"
                    name="password"
                    placeholder="Enter Your Password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={getFieldError("password")}
                    maxLength={50}
                    autoComplete="current-password"
                    required
                    disabled={isSubmitting}
                  />
                  {/* Subdomain Input */}
                  <CustomTextInput
                    type="text"
                    name="subDomain"
                    placeholder="School short name (e.g. abc123)"
                    value={formData.subDomain || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {/* Remember Me & Forgot Password */}
                  {/* <div className={styles.optionsRow}>
                    <label className={styles.rememberMe}>
                      <input
                        type="checkbox"
                        name="rememberMe"
                        checked={formData.rememberMe}
                        onChange={handleChange}
                        disabled={isSubmitting}
                      />
                      <span>Remember me</span>
                    </label>
                    <a href="/forgot-password" className={styles.forgotLink}>
                      Forgot Password?
                    </a>
                  </div> */}

                  {/* Buttons */}
                  <div className={styles.buttonGroup}>
                    {/* <button
                      type="button"
                      className={styles.homeButton}
                      onClick={handleHomeClick}
                      disabled={isSubmitting}
                    >
                      Home
                    </button> */}
                    <button
                      type="submit"
                      className={styles.loginButton}
                      disabled={!isFormValid || isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className={styles.buttonLoader}></span>
                          Loading...
                        </>
                      ) : (
                        "Login"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
